/**
 * Five9 screen-pop landing page.
 *
 * Five9 is configured with a URL like:
 *     https://uslscrm.trysynapse.ai/screen-pop?ani={{ANI}}
 * and opens it in the agent's browser when a call arrives. This page resolves the number and
 * either goes straight to the contact or asks the agent to choose — it NEVER guesses.
 *
 * Presupposition (owner, 2026-09-12): the agent is signed into both platforms in the same
 * browser. If the CRM session has lapsed, RequireAuth redirects to login preserving this
 * destination, so the pop completes after sign-in rather than being lost.
 *
 * Why a full page rather than a redirect-only route: an ambiguous or unmatched ANI needs
 * somewhere to say so. A bare redirect would either guess (unacceptable — it would open the
 * wrong member's record mid-call) or dump the agent on a blank contacts list.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  aniFromSearchParams,
  resolveContactByPhone,
  type Five9PhoneLookup,
} from "../services/five9.service";
import { formatPhoneNumber } from "../utils/formatters";
import Card from "../components/ui/Card";
import LoadingSpinner from "../components/common/LoadingSpinner";

const Shell: React.FC<{ children: React.ReactNode; ani: string | null }> = ({
  children,
  ani,
}) => (
  <div className="max-w-2xl mx-auto py-10 px-4 space-y-4">
    <div>
      <h1 className="text-2xl font-bold text-gray-900">Incoming call</h1>
      {ani && (
        <p className="mt-1 text-gray-600">
          Caller number{" "}
          <span className="font-medium text-gray-900">
            {formatPhoneNumber(ani) || ani}
          </span>
        </p>
      )}
    </div>
    {children}
  </div>
);

const ScreenPop: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const ani = useMemo(() => aniFromSearchParams(searchParams), [searchParams]);

  const [result, setResult] = useState<Five9PhoneLookup | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);

  // A single match navigates away. Guarded with a ref so a re-render (or React StrictMode's
  // double-invoked effect in dev) cannot fire navigate() twice and stack history entries.
  const navigatedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    if (!ani) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    resolveContactByPhone(ani)
      .then((r) => {
        if (cancelled) {
          return;
        }
        setResult(r);
        if (
          r.resolution === "matched" &&
          r.contact_id &&
          !navigatedRef.current
        ) {
          navigatedRef.current = true;
          // `replace` so the agent's Back button does not land them on this interstitial.
          navigate(`/contacts/${r.contact_id}?from=screenpop`, {
            replace: true,
          });
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [ani, navigate]);

  // No number in the URL at all — almost certainly a misconfigured Five9 screen-pop URL,
  // so say which parameters are accepted instead of a generic error.
  if (!ani) {
    return (
      <Shell ani={null}>
        <Card
          variant="outlined"
          padding="md"
          className="border-amber-200 bg-amber-50"
        >
          <p className="text-amber-900 font-medium">
            No caller number supplied
          </p>
          <p className="text-amber-800 text-sm mt-1">
            This page expects the caller&apos;s number in the URL, for example{" "}
            <code className="bg-white px-1 rounded">
              /screen-pop?ani=5558675309
            </code>
            . It also accepts{" "}
            <code className="bg-white px-1 rounded">phone</code>,{" "}
            <code className="bg-white px-1 rounded">callerid</code>,{" "}
            <code className="bg-white px-1 rounded">from</code> and{" "}
            <code className="bg-white px-1 rounded">number</code>.
          </p>
          <Link
            to="/contacts"
            className="inline-block mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Search contacts instead →
          </Link>
        </Card>
      </Shell>
    );
  }

  if (isLoading) {
    return (
      <Shell ani={ani}>
        <div className="flex items-center gap-3 text-gray-600">
          <LoadingSpinner />
          <span>Looking up the caller…</span>
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell ani={ani}>
        <Card
          variant="outlined"
          padding="md"
          className="border-red-200 bg-red-50"
        >
          <p className="text-red-800 font-medium">
            Could not look up this number
          </p>
          <p className="text-red-700 text-sm mt-1">
            {(error as Error)?.message ?? "Unknown error"}
          </p>
          <Link
            to="/contacts"
            className="inline-block mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Search contacts manually →
          </Link>
        </Card>
      </Shell>
    );
  }

  // Matched: the effect has already navigated. This is the brief frame before it happens.
  if (result?.resolution === "matched") {
    return (
      <Shell ani={ani}>
        <div className="flex items-center gap-3 text-gray-600">
          <LoadingSpinner />
          <span>Opening the contact…</span>
        </div>
      </Shell>
    );
  }

  if (result?.resolution === "ambiguous") {
    return (
      <Shell ani={ani}>
        <Card variant="outlined" padding="md">
          <p className="font-medium text-gray-900">
            {result.match_count} contacts share this number
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Choose the right one — the CRM will not guess.
            {result.truncated && " Showing the first few."}
          </p>
          <ul className="mt-4 divide-y divide-gray-200">
            {result.candidates.map((c) => (
              <li key={c.id}>
                <Link
                  to={`/contacts/${c.id}?from=screenpop`}
                  className="flex items-center justify-between py-3 group"
                >
                  <span>
                    <span className="font-medium text-primary-600 group-hover:text-primary-700">
                      {[c.first_name, c.last_name].filter(Boolean).join(" ") ||
                        "(no name)"}
                    </span>
                    <span className="block text-sm text-gray-500 break-all">
                      {c.email || "no email"}
                    </span>
                  </span>
                  <span className="text-xs text-gray-500 whitespace-nowrap ml-4">
                    {c.lifecycle_stage || c.status || ""}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      </Shell>
    );
  }

  // Unmatched — and the reason matters operationally, so each gets its own wording.
  const reason = result?.reason;
  const notDistinctive = reason === "ani_not_distinctive";
  const tooShort = reason === "ani_too_short";

  return (
    <Shell ani={ani}>
      <Card variant="outlined" padding="md">
        <p className="font-medium text-gray-900">
          {notDistinctive
            ? "This number cannot identify a caller"
            : tooShort
              ? "The caller's number was incomplete"
              : "No contact found for this number"}
        </p>
        <p className="text-sm text-gray-600 mt-1">
          {notDistinctive ? (
            <>
              It is stored on at least {result?.match_count_at_least} contacts,
              which usually means it is a placeholder or a company line rather
              than a personal number.
            </>
          ) : tooShort ? (
            <>
              Fewer than ten digits arrived — typically a withheld number or an
              internal extension. Search by name instead.
            </>
          ) : (
            <>
              Searched the last ten digits
              {result?.normalized ? ` (${result.normalized})` : ""} against
              mobile and primary phone numbers. Home and work numbers are not
              searched.
            </>
          )}
        </p>
        <div className="mt-4 flex items-center gap-4">
          <Link
            to="/contacts"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Search contacts →
          </Link>
        </div>
      </Card>
    </Shell>
  );
};

export default ScreenPop;
